/**
 * ControlsPanel - HUD help panel listing keyboard / mouse bindings using
 * composed Keystroke chips. Each row pairs a key chip with a description.
 */

import { BaseUIComponent } from './BaseUIComponent.js';
import { Keystroke } from './Keystroke.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface ControlBinding {
  /** Key label shown inside the chip (e.g. "W", "Shift", "LMB"). */
  key: string;
  /** Action description shown next to the chip. */
  description: string;
}

export interface ControlsPanelOptions extends BaseUIComponentOptions {
  /** Controls to display. */
  controls?: ControlBinding[];
  /** Panel title. */
  title?: string;
}

export class ControlsPanel extends BaseUIComponent<ControlsPanelOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Controls Panel',
    category: 'menu',
    summary: 'HUD help panel listing keyboard / mouse bindings.',
    useCases: ['controls', 'help', 'keybinds', 'tutorial', 'instructions'],
    optionsType: 'ControlsPanelOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/controls-help.html',
      styles: '@engine/assets/ui/styles/controls-help.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private listElement: HTMLElement | null = null;
  private keystrokes: Keystroke[] = [];

  protected override getAssetPaths() {
    return {
      templatePath: ControlsPanel.metadata.assetPaths.template,
      stylesPath: ControlsPanel.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<ControlsPanelOptions> {
    return {
      position: 'bottom-left',
      visible: true,
      customClasses: [],
      customStyles: {},
      controls: [
        { key: 'WASD', description: 'Move' },
        { key: 'Mouse', description: 'Look' },
        { key: 'LMB', description: 'Shoot' },
        { key: 'R', description: 'Reload' },
        { key: 'I', description: 'Inventory' },
        { key: 'P', description: 'Menu' },
      ],
      title: 'Controls',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      title: this.options.title,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-controls-panel') as HTMLElement | null;
    this.titleElement = this.layout.querySelector('[data-controls-title]') as HTMLElement | null;
    this.listElement = this.layout.querySelector('[data-controls-list]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    await this.renderRows();
  }

  /** Set custom position for the controls panel root. */
  public setCustomPosition(styles: Record<string, string>): void {
    this.setPosition(styles, '.ui-controls-panel');
  }

  /** Update the controls list and re-render the rows. */
  public updateControls(controls: ControlBinding[]): void {
    this.options.controls = controls;
    void this.renderRows();
  }

  private async renderRows(): Promise<void> {
    if (!this.listElement) return;

    // Destroy keystrokes from any previous render so we rebuild rows cleanly.
    for (const k of this.keystrokes) k.destroy();
    this.keystrokes = [];

    while (this.listElement.firstChild) {
      this.listElement.removeChild(this.listElement.firstChild);
    }

    for (const binding of this.options.controls) {
      const row = document.createElement('li');
      row.className = 'ui-controls-panel-row';

      const keySlot = document.createElement('span');
      keySlot.className = 'ui-controls-panel-key-slot';
      row.appendChild(keySlot);

      const description = document.createElement('span');
      description.className = 'ui-controls-panel-description';
      description.textContent = binding.description;
      row.appendChild(description);

      this.listElement.appendChild(row);

      const chip = await this.mountChild(Keystroke, { key: binding.key, size: 'small' }, keySlot);
      this.keystrokes.push(chip);
    }
  }

  protected override onDestroy(): void {
    // BaseUIComponent.destroy() will also clean up mountChild-tracked children.
    this.keystrokes = [];
    this.rootElement = null;
    this.titleElement = null;
    this.listElement = null;
  }
}
